/**
 * 章节API服务
 *
 * 封装章节相关的HTTP API调用
 * 提供CRUD操作、锁定、历史、diff和回滚功能
 * 使用 HttpOnly Cookie 进行认证。
 */

import { chapterApi, HttpError } from './chapterApi';
import type {
  Chapter,
  ChapterCreate,
  ChapterUpdate,
  ChapterHistory,
  DiffResult,
  LockRange,
  ChapterFilters
} from '../types/chapter';

export class ApiError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ValidationError extends ApiError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string) {
    super(message, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string) {
    super(message, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class InvalidRangeError extends ApiError {
  constructor(message: string) {
    super(message, 'INVALID_RANGE');
    this.name = 'InvalidRangeError';
  }
}

export class VersionNotFoundError extends ApiError {
  constructor(message: string) {
    super(message, 'VERSION_NOT_FOUND');
    this.name = 'VersionNotFoundError';
  }
}

function handleError(error: unknown): never {
  if (error instanceof HttpError) {
    switch (error.status) {
      case 404:
        throw new NotFoundError(error.message);
      case 403:
        throw new ForbiddenError(error.message);
      case 400:
        throw new ValidationError(error.message);
      default:
        throw new ApiError(error.message, error.code);
    }
  }
  throw error;
}

export class ChapterService {
  private workspaceId: number;

  constructor(
    _wsService?: unknown,
    _token?: string,
    workspaceId?: number
  ) {
    this.workspaceId = workspaceId || 0;
  }

  setWorkspaceId(workspaceId: number): void {
    this.workspaceId = workspaceId;
  }

  async listChapters(
    workspaceId: number,
    branchId?: number | null,
    includeCommon: boolean = true
  ): Promise<Chapter[]> {
    try {
      return await chapterApi.listChapters(workspaceId, branchId, includeCommon);
    } catch (error) {
      handleError(error);
    }
  }

  async createChapter(_workspaceId: number, data: ChapterCreate): Promise<Chapter> {
    try {
      return await chapterApi.createChapter(this.workspaceId, data);
    } catch (error) {
      handleError(error);
    }
  }

  async getChapter(_workspaceId: number, chapterId: number): Promise<Chapter> {
    try {
      return await chapterApi.getChapter(chapterId);
    } catch (error) {
      handleError(error);
    }
  }

  async updateChapter(_workspaceId: number, chapterId: number, data: ChapterUpdate): Promise<Chapter> {
    try {
      return await chapterApi.updateChapter(chapterId, data);
    } catch (error) {
      handleError(error);
    }
  }

  async deleteChapter(_workspaceId: number, chapterId: number): Promise<void> {
    try {
      await chapterApi.deleteChapter(chapterId);
    } catch (error) {
      handleError(error);
    }
  }

  async lockText(_workspaceId: number, chapterId: number, ranges: LockRange[], _action: 'lock' | 'unlock'): Promise<Chapter> {
    try {
      // 将 LockRange 元组格式转换为 API 期望的对象格式
      const formattedRanges = ranges.map(([start, end]) => ({ start, end }));
      return await chapterApi.lockText(chapterId, formattedRanges);
    } catch (error) {
      handleError(error);
    }
  }

  async lockChapter(_workspaceId: number, chapterId: number, action: 'lock' | 'unlock'): Promise<Chapter> {
    try {
      return await chapterApi.lockChapter(chapterId, action);
    } catch (error) {
      handleError(error);
    }
  }

  async getHistory(_workspaceId: number, chapterId: number, _page = 1, _pageSize = 20): Promise<ChapterHistory> {
    try {
      const result = await chapterApi.getHistory(chapterId);
      return {
        versions: result.versions,
        total: result.total,
        page: 1,
        page_size: result.total,
        total_pages: 1,
      };
    } catch (error) {
      handleError(error);
    }
  }

  async getDiff(_workspaceId: number, chapterId: number, version: number, _toVersion?: number): Promise<DiffResult[]> {
    try {
      const result = await chapterApi.getDiff(chapterId, version);
      return [{
        version: result.version,
        additions: result.additions,
        deletions: result.deletions,
        diff: result.diff,
      }];
    } catch (error) {
      handleError(error);
    }
  }

  async rollback(_workspaceId: number, chapterId: number, versionNum: number): Promise<Chapter> {
    try {
      return await chapterApi.rollback(chapterId, versionNum);
    } catch (error) {
      handleError(error);
    }
  }

  async generateAbstract(_workspaceId: number, chapterId: number, _forceRegenerate = false): Promise<Chapter> {
    try {
      await chapterApi.generateAbstract(chapterId);
      return await chapterApi.getChapter(chapterId);
    } catch (error) {
      handleError(error);
    }
  }

  async getAbstract(_workspaceId: number, chapterId: number): Promise<{ chapter_id: number; chapter_abstract: string | null; abstract_updated_at: string | null }> {
    try {
      const chapter = await chapterApi.getChapter(chapterId);
      return {
        chapter_id: chapter.id,
        chapter_abstract: chapter.chapter_abstract,
        abstract_updated_at: chapter.abstract_updated_at,
      };
    } catch (error) {
      handleError(error);
    }
  }
}

export default ChapterService;
